import { heroMeta, pursuitDealMeta } from '../recognition.js'
import { partnershipMeta } from '../cheer.js'
import styles from './pursuit.module.css'

/**
 * THE COMMUNITY HERO BADGE — the read view, everywhere a police or fire
 * department is rendered (the gaps table, the board card, the drawer).
 *
 * It is driven entirely by the associated hero DEAL the backend read alongside
 * the department. No nomination means no deal, and the badge says NO HERO —
 * loudly, in the gap style, because a department without a hero is exactly the
 * work this space exists to surface. Nothing here is a flag or a stub: the
 * moment a hero deal is associated to a department in HubSpot, this renders it.
 */
export default function HeroBadge({ record, showName = true }) {
  const meta = heroMeta(record)
  if (!meta.present) {
    return <span className={styles.badgeGap} title="No Community Hero nominated for this department yet">NO HERO</span>
  }
  return (
    <span className={styles.heroCell}>
      <span
        className={styles.statusChip}
        style={{ color: meta.color, borderColor: meta.color + '55', background: meta.color + '12' }}
        title={meta.active ? 'This department has an active Community Hero' : 'Nomination closed — the slot is open again'}
      >
        ★ {meta.label}
      </span>
      {showName && meta.name && (
        <span className={styles.heroName}>
          {meta.name}
          {meta.years != null && <span className={styles.heroYears}>{meta.years} yrs</span>}
        </span>
      )}
    </span>
  )
}

/**
 * THE GREEK PURSUIT CHIP — the product being pursued and how it is going. A
 * chapter with no pursuit deal says so rather than rendering an empty chip.
 */
export function PursuitChip({ record }) {
  const meta = pursuitDealMeta(record)
  if (!meta.present) {
    return <span className={styles.badgeGap} title="No product pursuit opened at this organization yet">NO PURSUIT</span>
  }
  return (
    <span
      className={styles.statusChip}
      style={{ color: meta.color, borderColor: meta.color + '55', background: meta.color + '12' }}
    >
      {meta.won ? '✓ ' : ''}{meta.label}
    </span>
  )
}

/**
 * THE CHEER GYM PARTNERSHIP CELL — where the gym is on the pipeline, with BOTH
 * halves spelled out beside it.
 *
 * The stage chip alone would let "Contract signed" read as a partnership, which
 * it is not: a partnership is a signed contract AND an exclusive, fully-branded
 * jewelry program. So the two halves are always drawn, and the missing one is
 * drawn in the gap style — that is the next job on this gym.
 */
export function PartnershipCell({ record }) {
  const meta = partnershipMeta(record)
  return (
    <span className={styles.heroCell}>
      <span
        className={styles.statusChip}
        style={{ color: meta.color, borderColor: meta.color + '55', background: meta.color + '12' }}
        title={meta.hint}
      >
        {meta.partner ? '✓ ' : ''}{meta.label}
      </span>
      <span className={styles.heroName}>
        <span className={meta.signed ? undefined : styles.tGap}>{meta.signed ? 'contract' : 'no contract'}</span>
        {' · '}
        <span className={meta.jewelry.live ? undefined : styles.tGap}>
          {meta.jewelry.live ? 'jewelry live' : 'no jewelry'}
        </span>
      </span>
    </span>
  )
}

/*
 * THE PER-ORG ACTION BUTTON used to live here. It is gone (v1.30) and its job
 * belongs to `NextStep.jsx`: a bare "Nominate" button told a rep WHAT they
 * could do but never WHICH records to do it to or what happens after, which is
 * the data-staring problem the guided pathway exists to fix. The next-step
 * component still renders the same action — inside a numbered step, with the
 * one-line consequence — and one-active-hero-per-precinct is decided by the
 * model (`nextStepFor`, and the backend, which is the one that counts) rather
 * than by a disabled button.
 */
